import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent, ReactNode, RefObject } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import ShareModal from 'components/Common/Modals/ShareModal'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { trackClientEvent } from 'analytics/analytics'
import useCheckoutShareConfig from 'hooks/useCheckoutShareConfig'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineShareAltIcon from 'LuxKit/Icons/line/LineShareAltIcon'
import RichTooltip from 'LuxKit/RichTooltip'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import zIndex from 'styles/tools/z-index'

const SHARE_TOOLTIP_AUTO_CLOSE_DELAY_MS = 5000

const ShareTooltipTrigger = styled.span`
  display: inline-flex;
`

const CruiseShareTooltip = styled(RichTooltip)`
  z-index: ${zIndex.stickyHeader + 1};

  &[role='tooltip'] {
    max-width: min(clamp(${rem(260)}, 90vw, ${rem(420)}), calc(100vw - ${rem(32)}));
  }
`

interface Props {
  showCruiseShareTooltip?: boolean
}

interface CruiseShareButtonProps {
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  tooltipAnchorRef?: RefObject<HTMLSpanElement | null>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

interface CruiseShareTextButtonProps extends CruiseShareButtonProps {
  children: ReactNode
}

function CruiseShareTextButton({ onClick, tooltipAnchorRef, onMouseEnter, onMouseLeave, children }: CruiseShareTextButtonProps) {
  return (
    <ShareTooltipTrigger ref={tooltipAnchorRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <TextButton
        kind="tertiary"
        startIcon={<LineShareAltIcon aria-hidden="true" />}
        onClick={onClick}
      >
        {children}
      </TextButton>
    </ShareTooltipTrigger>
  )
}

function CruiseShareIconButton({ onClick, tooltipAnchorRef }: CruiseShareButtonProps) {
  return (
    <ShareTooltipTrigger ref={tooltipAnchorRef}>
      <IconButton kind="tertiary" size="large" onClick={onClick}>
        <LineShareAltIcon aria-hidden="true" />
      </IconButton>
    </ShareTooltipTrigger>
  )
}

function CruiseCheckoutShareButton({ showCruiseShareTooltip = false }: Props) {
  const intl = useIntl()
  const shareConfig = useCheckoutShareConfig({ includeSharedBy: true })
  const [isShareTooltipOpen, setIsShareTooltipOpen] = useState(false)
  const [isShareTooltipHovered, setIsShareTooltipHovered] = useState(false)
  const hasSeenShareTooltip = useRef(false)
  const isDesktopShareButton = useScreenSizeUp('largeDesktop')
  const desktopShareButtonRef = useRef<HTMLSpanElement>(null)
  const mobileShareButtonRef = useRef<HTMLSpanElement>(null)
  const { variation } = useExperiment(Experiments.croShareSocietePoints)

  const closeShareTooltip = useCallback(() => {
    setIsShareTooltipOpen(false)
  }, [])

  const handleShareTooltipMouseEnter = useCallback(() => {
    if (isShareTooltipOpen) return
    setIsShareTooltipHovered(true)
  }, [isShareTooltipOpen])

  const handleShareTooltipMouseLeave = useCallback(() => {
    setIsShareTooltipHovered(false)
  }, [])

  const trackShareButtonClick = useCallback(() => {
    trackClientEvent({
      subject: 'share',
      action: 'pressed',
      category: 'offer_page',
      type: 'interaction',
      ...(variation && { label: variation }),
    })
  }, [variation])

  useEffect(() => {
    if (!showCruiseShareTooltip) {
      closeShareTooltip()
      return
    }

    if (hasSeenShareTooltip.current) return

    setIsShareTooltipOpen(true)
    hasSeenShareTooltip.current = true
    const timerId = window.setTimeout(closeShareTooltip, SHARE_TOOLTIP_AUTO_CLOSE_DELAY_MS)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [closeShareTooltip, showCruiseShareTooltip])

  useEffect(() => {
    if (!isDesktopShareButton) {
      setIsShareTooltipHovered(false)
    }
  }, [isDesktopShareButton])

  const shareTooltipDescription = intl.formatMessage({
    id: '_Fvds3nMm1tZNSWTN-a9',
    defaultMessage: 'Share your cruise plans with family and friends! They’ll see exactly what you’ve chosen.',
    description: 'Tooltip message on cruise checkout share button at traveller selection step',
  })
  const shareModalTitle = shareConfig.shareTitle
  const shareButtonLabel = intl.formatMessage({
    id: 'R58guJ1O4ouA2CDXNYso',
    defaultMessage: 'Share',
    description: 'Button text to share the current offer via social media or a shareable link',
  })
  const isDesktopShareTooltipVisible = isShareTooltipOpen || isShareTooltipHovered

  return (
    <>
      <CSSBreakpoint min="largeDesktop">
        <CruiseShareTooltip
          portaled
          placement="top-end"
          anchorRef={desktopShareButtonRef as RefObject<HTMLElement | null>}
          open={isDesktopShareButton ? isDesktopShareTooltipVisible : false}
          dismissible={isShareTooltipOpen}
          onDismiss={closeShareTooltip}
          closeOnOutsideClick
          description={shareTooltipDescription}
        >
          <ShareModal shareConfig={shareConfig} hideEmail modalTitle={shareModalTitle}>
            <CruiseShareTextButton
              onClick={trackShareButtonClick}
              tooltipAnchorRef={desktopShareButtonRef}
              onMouseEnter={handleShareTooltipMouseEnter}
              onMouseLeave={handleShareTooltipMouseLeave}
            >
              {shareButtonLabel}
            </CruiseShareTextButton>
          </ShareModal>
        </CruiseShareTooltip>
      </CSSBreakpoint>
      <CSSBreakpoint max="desktop">
        <CruiseShareTooltip
          portaled
          placement="top-end"
          anchorRef={mobileShareButtonRef as RefObject<HTMLElement | null>}
          open={!isDesktopShareButton ? isShareTooltipOpen : false}
          dismissible={isShareTooltipOpen}
          onDismiss={closeShareTooltip}
          closeOnOutsideClick
          description={shareTooltipDescription}
        >
          <ShareModal shareConfig={shareConfig} hideEmail modalTitle={shareModalTitle}>
            <CruiseShareIconButton tooltipAnchorRef={mobileShareButtonRef} />
          </ShareModal>
        </CruiseShareTooltip>
      </CSSBreakpoint>
    </>
  )
}

export default CruiseCheckoutShareButton
